import { convertMoneyToNumber, convertUnitToSubUnit, unitDivisor } from './currency';
import { daysBetween, getMomentFromDate } from './dates';
import Decimal from 'decimal.js';
import { types as sdkTypes } from '../util/sdkLoader';
import moment from 'moment';
import { BOOKING_POSSIBLE_TIME_RANGE } from '../constants/booking';
import { transitions } from '../transactions/transactionProcessBooking';

const { Money } = sdkTypes;

export const checkIfDatesOverlap = (startDate1, endDate1, startDate2, endDate2) => {
  const startsAtA = new Date(startDate1);
  const endsAtA = new Date(endDate1);
  const startsAtB = new Date(startDate2);
  const endsAtB = new Date(endDate2);

  return startsAtA < endsAtB && startsAtB < endsAtA;
  // Not overlapping if startsAtA >= endsAtB || endsAtA <= startsAtB

  // startsAtA as {
  // endsAtA as }
  // startsAtB as [
  // endsAtB as ]

  // Not overlapping cases
  // -----[-----]--{-}-
  // -{-}-[-----]------

  // Overlapping
  // --{--[---}-]------
  // -----[-{---]--}---
  // --{--[-----]--}---
  // -----[{---}]------
};

export const checkIfEventDateIsPossible = dayMoment => {
  const start = moment()
    .add(
      BOOKING_POSSIBLE_TIME_RANGE.startPeriod.amount,
      BOOKING_POSSIBLE_TIME_RANGE.startPeriod.unit
    )
    .startOf('day');
  const end = moment().add(
    BOOKING_POSSIBLE_TIME_RANGE.endPeriod.amount,
    BOOKING_POSSIBLE_TIME_RANGE.endPeriod.unit
  );

  return dayMoment.isBetween(start, end);
};

export const createEventFromBooking = (intl, booking) => {
  const { _id, attributes } = booking;

  const { metadata, protectedData } = attributes || {};

  const { dateOfEvent } = metadata || {};
  const { eventType } = protectedData || {};

  return {
    title: intl.formatMessage({ id: `General.${eventType}` }),
    start: new Date(dateOfEvent),
    end: new Date(dateOfEvent),
    resource: {
      id: _id,
      booking,
    },
  };
};

export const createBookingDate = (bookingDate, bookingTime) =>
  bookingDate && bookingTime
    ? getMomentFromDate(bookingDate)
        .hours(bookingTime.slice(0, 2))
        .minutes(bookingTime.slice(3))
        .toDate()
    : null;

export const estimatedTotalPrice = (lineItems, marketplaceCurrency) => {
  const numericTotalPrice = lineItems?.reduce((sum, lineItem) => {
    const numericPrice = convertMoneyToNumber(lineItem?.lineTotal);
    return new Decimal(numericPrice).add(sum);
  }, new Decimal(0));

  // All the lineItems should have same currency so we can use the first one to check that
  // In case there are no lineItems we use currency from config.js as default
  const currency =
    lineItems[0] && lineItems[0].unitPrice ? lineItems[0].unitPrice.currency : marketplaceCurrency;

  return new Money(
    convertUnitToSubUnit(numericTotalPrice.toNumber(), unitDivisor(currency)),
    currency
  );
};

export const parseProtectedDataLineItems = (lineItems, currency) => {
  const lineItemsWithTotals = lineItems.map(lineItem => {
    const { lineTotal, unitPrice, quantity, percentage } = lineItem;

    return {
      ...lineItem,
      unitPrice: unitPrice ? new Money(unitPrice, currency) : null,
      lineTotal: lineTotal ? new Money(lineTotal, currency) : null,
      quantity: quantity ? new Decimal(quantity) : null,
      percentage: percentage ? new Decimal(percentage) : null,
      reversal: false,
    };
  });
  return lineItemsWithTotals;
};

export const removeTypeFromProtectedDataLocation = location => {
  const { selectedPlace, search } = location;

  const { bounds, origin, address, line1, name, postalCode, city } = selectedPlace || {};

  return {
    search,
    selectedPlace: {
      address,
      line1,
      name,
      postalCode,
      city,
      origin: {
        lat: origin.lat,
        lng: origin.lng,
      },
      bounds: bounds
        ? {
            ne: { lat: bounds.ne.lat, lng: bounds.ne.lng },
            sw: { lat: bounds.sw.lat, lng: bounds.sw.lng },
          }
        : null,
    },
  };
};

/**
 - 100% of artist fee is due if cancellation occurs within 30 days of event date.
 - 75% of artist fee is due if cancellation occurs within 31-91 days of event date.
 - 50% of artist fee is due if cancellation occurs within 92-181 days of event date.
 */
export const getCancellationFeePercent = eventDate => {
  const now = new Date();

  let daysDiff;

  try {
    daysDiff = daysBetween(now, eventDate);
  } catch (error) {}

  if (typeof daysDiff === 'undefined') return 100;

  if (daysDiff <= 30) return 100;

  if (daysDiff > 30 && daysDiff <= 91) return 75;

  return 50;
};

export const calculateVATAmount = grossValue => {
  return grossValue / 6;
};

export const getInvoiceDate = (transactionType, dateOfEvent) => {
  const eventDate = moment(dateOfEvent);

  switch (transactionType) {
    case 1: // super short
      return eventDate.subtract(7, 'days').toDate();

    case 2: // short
      return eventDate.subtract(30, 'days').toDate();

    case 3: // default
    default:
      return eventDate.subtract(60, 'days').toDate();
  }
};

export const getPaymentDueByDate = (transactionType, dateOfEvent) => {
  const eventDate = moment(dateOfEvent);

  switch (transactionType) {
    case 1: // super short
      return eventDate.subtract(7, 'days').toDate();

    case 2: // short
      return eventDate.subtract(14, 'days').toDate();

    case 3: // default
    default:
      return eventDate.subtract(30, 'days').toDate();
  }
};

/**
 * @typedef {Object} TransactionPaymentTerms
 * @property {1|2|3} type
 *   Booking flow type:
 *   - 1 = super short notice
 *   - 2 = short notice
 *   - 3 = standard
 * @property {'super_short_notice'|'short_notice'|'standard'} flowName
 * @property {string} quoteDate
 *   ISO string for the latest quote-related transition date.
 * @property {string} eventDate
 *   ISO string for the event date.
 * @property {string|null} quoteExpiresAt
 *   ISO string when the quote expires or becomes no longer actionable.
 * @property {string|null} depositDueAt
 *   ISO string when the deposit is due, or null when not applicable.
 * @property {string|null} depositRefundableUntil
 *   ISO string until when the deposit is refundable, or null when not applicable.
 * @property {string|null} finalInvoiceAt
 *   ISO string when the final invoice should be issued, or null when not applicable.
 * @property {string|null} mainPaymentDueAt
 *   ISO string when the main payment is due, or null when not applicable.
 * @property {string|null} fullPaymentDueAt
 *   ISO string when full payment is due, or null when not applicable.
 * @property {boolean} isRefundable
 *   Whether the payment terms include a refundable deposit.
 */

/**
 * Returns booking flow and all payment deadlines based on the latest quote event.
 *
 * @param {Object} transaction - Sharetribe transaction object.
 * @returns {TransactionPaymentTerms|null}
 */
export const getTransactionPaymentTerms = transaction => {
  const metadata = transaction?.attributes?.metadata || {};
  const pastTransitions = transaction?.attributes?.transitions || [];

  const eventDateRaw = metadata.dateOfEvent;

  if (!eventDateRaw || !moment(eventDateRaw).isValid()) {
    return null;
  }

  const relevantTransitions = pastTransitions.filter(t =>
    [transitions.SEND_QUOTE, transitions.SEND_QUOTE_UPDATE, transitions.REOPEN_QUOTE].includes(
      t?.transition
    )
  );

  if (!relevantTransitions.length) {
    return null;
  }

  const lastQuoteTransition = relevantTransitions.reduce((latest, current) =>
    moment(current.createdAt).isAfter(moment(latest.createdAt)) ? current : latest
  );

  const quoteDate = moment(lastQuoteTransition.createdAt);
  const eventDate = moment(eventDateRaw);

  if (!quoteDate.isValid() || !eventDate.isValid()) {
    return null;
  }

  const diffInDays = eventDate
    .clone()
    .startOf('day')
    .diff(quoteDate.clone().startOf('day'), 'days');

  let flowType = 0;
  let flowName = 'standard';

  if (diffInDays < 30) {
    flowType = 1;
    flowName = 'super_short_notice';
  } else if (diffInDays < 60) {
    flowType = 2;
    flowName = 'short_notice';
  } else {
    flowType = 3;
    flowName = 'standard';
  }

  /** @type {TransactionPaymentTerms} */
  const result = {
    type: flowType,
    flowName,
    quoteDate: quoteDate.toISOString(),
    eventDate: eventDate.toISOString(),
    quoteExpiresAt: null,
    depositDueAt: null,
    depositRefundableUntil: null,
    finalInvoiceAt: null,
    mainPaymentDueAt: null,
    fullPaymentDueAt: null,
    isRefundable: flowType !== 1,
  };

  if (flowType === 3) {
    const depositDueAt = quoteDate.clone().add(14, 'days');
    const depositRefundableUntil = depositDueAt.clone().add(48, 'hours');
    const invoiceThreshold = eventDate.clone().subtract(60, 'days');
    const finalInvoiceAt = moment.max(invoiceThreshold, depositRefundableUntil);
    const mainPaymentDueAt = eventDate.clone().subtract(30, 'days');

    result.depositDueAt = depositDueAt.toISOString();
    result.depositRefundableUntil = depositRefundableUntil.toISOString();
    result.finalInvoiceAt = finalInvoiceAt.toISOString();
    result.mainPaymentDueAt = mainPaymentDueAt.toISOString();
    result.quoteExpiresAt = depositDueAt.toISOString();
  }

  if (flowType === 2) {
    const depositDueAt = quoteDate.clone().add(7, 'days');
    const depositRefundableUntil = depositDueAt.clone().add(48, 'hours');
    const invoiceThreshold = eventDate.clone().subtract(30, 'days');
    const finalInvoiceAt = moment.max(invoiceThreshold, depositRefundableUntil);
    const mainPaymentDueAt = eventDate.clone().subtract(14, 'days');

    result.depositDueAt = depositDueAt.toISOString();
    result.depositRefundableUntil = depositRefundableUntil.toISOString();
    result.finalInvoiceAt = finalInvoiceAt.toISOString();
    result.mainPaymentDueAt = mainPaymentDueAt.toISOString();
    result.quoteExpiresAt = depositDueAt.toISOString();
  }

  if (flowType === 1) {
    const quoteDeadline = quoteDate.clone().add(7, 'days');
    const eventDeadline = eventDate.clone().subtract(3, 'days');
    const fullPaymentDueAt = moment.min(quoteDeadline, eventDeadline);

    result.fullPaymentDueAt = fullPaymentDueAt.toISOString();
    result.quoteExpiresAt = fullPaymentDueAt.toISOString();
  }

  return result;
};

/**
 * @typedef {Object} TransactionTransition
 * @property {string} transition - Transition identifier.
 * @property {string} createdAt - ISO timestamp when the transition occurred.
 * @property {'customer' | 'provider' | 'system'} by
 *   Actor who triggered the transition.
 */

/**
 * @typedef {Object} TransactionAttributes
 * @property {TransactionTransition[]} [transitions=[]]
 *   List of transitions that have already occurred for the transaction.
 */

/**
 * @typedef {Object} Transaction
 * @property {TransactionAttributes} [attributes]
 */

/**
 * Checks whether the deposit has already been paid for a transaction.
 *
 * A deposit is considered paid once the transaction passes the
 * `ACCEPTED_REFUNDABLE_PERIOD_OVER` transition.
 *
 * @param {Transaction} transaction - Sharetribe transaction object.
 * @returns {boolean} Returns `true` if the deposit has been paid, otherwise `false`.
 */
export const hasDepositBeenPaid = transaction => {
  const { transitions: passedTransitions = [] } = transaction?.attributes || {};

  return passedTransitions.some(t => t.transition === transitions.ACCEPTED_REFUNDABLE_PERIOD_OVER);
};

/**
 * Checks whether a booking is considered short notice.
 *
 * A booking is treated as short notice when the event date
 * is less than 30 days from today.
 *
 * @param {string} eventDateIso - Event date as an ISO string.
 * @returns {boolean} Returns `true` if the event is within 30 days, otherwise `false`.
 */
export const isBookingShortNotice = eventDateIso => {
  if (!eventDateIso || !moment(eventDateIso).isValid()) {
    return false;
  }

  const eventDate = moment(eventDateIso);
  const today = moment();
  return eventDate.diff(today, 'days') < 30;
};
